/**
 * CategoryCelebration - Confetti + Haptic Celebration Component
 *
 * Fires when the user's revised score crosses a category boundary
 * during the rewrite session. Graduated celebrations prevent fatigue:
 *
 * 1. First crossing: Full confetti burst (12 particles, 800ms) + success haptic
 * 2. Subsequent crossings: Subtle green flash overlay (200ms) + medium haptic
 * 3. Never celebrates score increases (worsening)
 *
 * Non-blocking: uses pointerEvents="none" so users can interact through it.
 * Cross-platform: haptics silently skip on web.
 */

import { useEffect, useCallback, useRef, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  withTiming,
  withDelay,
  runOnJS,
  Easing,
} from 'react-native-reanimated';
import { triggerNotification, triggerImpact } from '@/lib/haptics';
import * as Haptics from 'expo-haptics';

type CelebrationCategory = 'safe' | 'caution' | 'high_drama';

interface CategoryCelebrationProps {
  /** Whether to trigger celebration */
  trigger: boolean;
  /** Which category was reached */
  category: CelebrationCategory | null;
  /** Whether this is the first crossing in the session */
  isFirstCrossing: boolean;
  /** Callback when celebration animation completes */
  onComplete?: () => void;
  /** Test ID for testing */
  testID?: string;
}

const PARTICLE_COUNT = 12;
const CELEBRATION_DURATION = 800;
const FLASH_DURATION = 200;

/** Colors per category for confetti particles */
const CATEGORY_COLORS: Record<CelebrationCategory, readonly string[]> = {
  safe: ['#16A34A', '#22C55E', '#4ADE80', '#86EFAC'],
  caution: ['#EAB308', '#FACC15', '#FDE047', '#FEF08A'],
  high_drama: ['#F97316', '#FB923C', '#FDBA74', '#FED7AA'],
};

/** A single confetti particle with animated position and opacity */
function ConfettiParticle({
  index,
  color,
  testID,
}: {
  index: number;
  color: string;
  testID?: string;
}) {
  const opacity = useSharedValue(0);
  const translateY = useSharedValue(-20);
  const translateX = useSharedValue(0);

  useEffect(() => {
    // Stagger particle starts slightly
    const delay = index * 30;
    // Random horizontal spread: -80 to +80
    const xTarget = (Math.random() - 0.5) * 160;

    translateX.value = xTarget * 0.3;
    opacity.value = withDelay(delay, withTiming(1, { duration: 100 }));
    translateY.value = withDelay(
      delay,
      withTiming(120 + Math.random() * 60, {
        duration: CELEBRATION_DURATION - delay,
        easing: Easing.out(Easing.quad),
      }),
    );
    translateX.value = withDelay(
      delay,
      withTiming(xTarget, {
        duration: CELEBRATION_DURATION - delay,
        easing: Easing.out(Easing.quad),
      }),
    );

    // Fade out in the last 300ms
    opacity.value = withDelay(
      delay + CELEBRATION_DURATION - 400,
      withTiming(0, { duration: 300 }),
    );
  }, [index, opacity, translateX, translateY]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.value,
    transform: [
      { translateY: translateY.value },
      { translateX: translateX.value },
    ],
  }));

  return (
    <Animated.View
      testID={testID ? `${testID}-particle-${index}` : undefined}
      style={[
        styles.particle,
        { backgroundColor: color },
        animatedStyle,
      ]}
    />
  );
}

/**
 * CategoryCelebration - Overlay component for category crossing celebrations.
 *
 * Renders as an absolute-positioned overlay with pointerEvents="none".
 */
export function CategoryCelebration({
  trigger,
  category,
  isFirstCrossing,
  onComplete,
  testID = 'category-celebration',
}: CategoryCelebrationProps) {
  const flashOpacity = useSharedValue(0);
  const hasTriggeredRef = useRef(false);

  // Generate stable particle colors
  const particleColors = useMemo(() => {
    if (!category) return [];
    const colors = CATEGORY_COLORS[category] ?? CATEGORY_COLORS.caution;
    return Array.from({ length: PARTICLE_COUNT }, (_, i) => colors[i % colors.length]);
  }, [category]);

  const handleComplete = useCallback(() => {
    onComplete?.();
  }, [onComplete]);

  useEffect(() => {
    if (!trigger || !category) return;

    // Prevent re-triggering for same crossing
    if (hasTriggeredRef.current) return;
    hasTriggeredRef.current = true;

    if (isFirstCrossing) {
      // Full celebration: success haptic
      triggerNotification(Haptics.NotificationFeedbackType.Success);

      // Schedule completion callback after animation
      const timeout = setTimeout(() => {
        runOnJS(handleComplete)();
      }, CELEBRATION_DURATION + 100);

      return () => clearTimeout(timeout);
    } else {
      // Subtle flash: medium impact haptic
      triggerImpact(Haptics.ImpactFeedbackStyle.Medium);

      // Green flash overlay
      flashOpacity.value = withTiming(0.3, { duration: FLASH_DURATION }, () => {
        flashOpacity.value = withTiming(0, { duration: FLASH_DURATION }, () => {
          runOnJS(handleComplete)();
        });
      });
    }
  }, [trigger, category, isFirstCrossing, flashOpacity, handleComplete]);

  // Reset trigger guard when trigger goes false
  useEffect(() => {
    if (!trigger) {
      hasTriggeredRef.current = false;
    }
  }, [trigger]);

  const flashStyle = useAnimatedStyle(() => ({
    opacity: flashOpacity.value,
  }));

  if (!trigger || !category) return null;

  return (
    <View
      style={styles.container}
      pointerEvents="none"
      testID={testID}
    >
      {isFirstCrossing ? (
        // Full confetti burst
        <View style={styles.confettiContainer}>
          {particleColors.map((color, index) => (
            <ConfettiParticle
              key={index}
              index={index}
              color={color}
              testID={testID}
            />
          ))}
        </View>
      ) : (
        // Subtle green flash overlay
        <Animated.View
          style={[styles.flashOverlay, flashStyle]}
          testID={`${testID}-flash`}
        />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    ...StyleSheet.absoluteFill,
    overflow: 'hidden',
    zIndex: 10,
  },
  confettiContainer: {
    ...StyleSheet.absoluteFill,
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: 10,
  },
  particle: {
    position: 'absolute',
    width: 8,
    height: 8,
    borderRadius: 4,
  },
  flashOverlay: {
    ...StyleSheet.absoluteFill,
    backgroundColor: '#16A34A',
    borderRadius: 16,
  },
});
